/**
 * DateRangeSelector Component
 * Dropdown selector for date range with presets and custom range options
 */

import React, { useState, useRef, useEffect } from 'react'
import { Calendar, ChevronDown, X } from 'lucide-react'
import { format, isValid } from 'date-fns'
import { useDateRange, DATE_PRESETS } from '../contexts/DateRangeContext'
import clsx from 'clsx'

const DateRangeSelector = () => {
  const {
    dateRange,
    isCustomRange,
    setPresetRange,
    setCustomRange,
    getFormattedRange
  } = useDateRange()

  const [isOpen, setIsOpen] = useState(false)
  const [showCustomPicker, setShowCustomPicker] = useState(false)
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const dropdownRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
        setShowCustomPicker(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Initialize custom dates when opening custom picker - always pre-populate with current range
  useEffect(() => {
    if (showCustomPicker && dateRange.startDate && dateRange.endDate) {
      setCustomStart(format(dateRange.startDate, 'yyyy-MM-dd'))
      setCustomEnd(format(dateRange.endDate, 'yyyy-MM-dd'))
    }
  }, [showCustomPicker, dateRange])

  const handlePresetClick = (presetKey) => {
    setPresetRange(presetKey)
    setIsOpen(false)
    setShowCustomPicker(false)
  }

  const handleCustomRangeSubmit = (e) => {
    e.preventDefault()
    const startDate = new Date(customStart)
    const endDate = new Date(customEnd)

    if (isValid(startDate) && isValid(endDate) && startDate <= endDate) {
      setCustomRange(startDate, endDate)
      setIsOpen(false)
      setShowCustomPicker(false)
    }
  }

  const handleClearCustomRange = () => {
    setCustomStart('')
    setCustomEnd('')
    setShowCustomPicker(false)
  }

  const formattedRange = getFormattedRange()

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          "flex items-center space-x-1.5 px-2.5 py-1 text-xs rounded-md border transition-all",
          "bg-transparent",
          "border-slate-300 dark:border-slate-700/50",
          "hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-slate-300",
          "focus:outline-none focus:ring-2 focus:ring-blue-500",
          isOpen && "ring-2 ring-blue-500 dark:ring-blue-400"
        )}
      >
        <Calendar className="w-3 h-3 text-slate-500 dark:text-slate-400 flex-shrink-0" />
        <span className="text-slate-600 dark:text-slate-400 hidden sm:inline">
          {formattedRange}
        </span>
        <span className="text-slate-600 dark:text-slate-400 sm:hidden">
          {dateRange.preset && DATE_PRESETS[dateRange.preset]
            ? DATE_PRESETS[dateRange.preset].label
            : formattedRange.length > 15
              ? 'Custom'
              : formattedRange}
        </span>
        <ChevronDown className={clsx(
          "w-3 h-3 text-slate-500 transition-transform flex-shrink-0",
          isOpen && "transform rotate-180"
        )} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-lg bg-white dark:bg-slate-800 shadow-lg ring-1 ring-black ring-opacity-5 z-50">
          {!showCustomPicker ? (
            <>
              {/* Preset Options */}
              <div className="py-2">
                <div className="px-4 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Quick Select
                </div>
                {Object.entries(DATE_PRESETS).map(([key, preset]) => (
                  <button
                    key={key}
                    onClick={() => handlePresetClick(key)}
                    className={clsx(
                      "w-full text-left px-4 py-2 text-sm transition-colors",
                      "hover:bg-slate-100 dark:hover:bg-slate-700",
                      dateRange.preset === key
                        ? "bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 font-medium"
                        : "text-slate-700 dark:text-slate-200"
                    )}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              {/* Divider */}
              <div className="border-t border-slate-200 dark:border-slate-700"></div>

              {/* Custom Range Option */}
              <div className="py-2">
                <button
                  onClick={() => setShowCustomPicker(true)}
                  className={clsx(
                    "w-full text-left px-4 py-2 text-sm transition-colors",
                    "hover:bg-slate-100 dark:hover:bg-slate-700",
                    isCustomRange && !dateRange.preset
                      ? "bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 font-medium"
                      : "text-slate-700 dark:text-slate-200"
                  )}
                >
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>Custom Range</span>
                  </div>
                  {isCustomRange && !dateRange.preset && (
                    <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {formattedRange}
                    </div>
                  )}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Custom Date Picker */}
              <form onSubmit={handleCustomRangeSubmit} className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                    Select Date Range
                  </h3>
                  <button
                    type="button"
                    onClick={handleClearCustomRange}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                      className={clsx(
                        "w-full px-3 py-2 text-sm rounded-md border",
                        "bg-white dark:bg-slate-900",
                        "border-slate-200 dark:border-slate-700",
                        "text-slate-700 dark:text-slate-200",
                        "focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400",
                        "placeholder:text-slate-400 dark:placeholder:text-slate-500",
                        "[color-scheme:light] dark:[color-scheme:dark]"
                      )}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      min={customStart}
                      className={clsx(
                        "w-full px-3 py-2 text-sm rounded-md border",
                        "bg-white dark:bg-slate-900",
                        "border-slate-200 dark:border-slate-700",
                        "text-slate-700 dark:text-slate-200",
                        "focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400",
                        "placeholder:text-slate-400 dark:placeholder:text-slate-500",
                        "[color-scheme:light] dark:[color-scheme:dark]"
                      )}
                      required
                    />
                  </div>
                </div>

                <div className="flex space-x-2 mt-4">
                  <button
                    type="button"
                    onClick={() => setShowCustomPicker(false)}
                    className={clsx(
                      "flex-1 px-3 py-2 text-sm font-medium rounded-md",
                      "bg-slate-100 dark:bg-slate-700",
                      "text-slate-700 dark:text-slate-200",
                      "hover:bg-slate-200 dark:hover:bg-slate-600",
                      "transition-colors"
                    )}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={clsx(
                      "flex-1 px-3 py-2 text-sm font-medium rounded-md",
                      "bg-blue-600 hover:bg-blue-700",
                      "text-white",
                      "transition-colors"
                    )}
                  >
                    Apply
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default DateRangeSelector